import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import { useShop } from "@/context/ShopContext";

const EXAMPLE = "4 Ragavan, Nimble Pilferer\n1 The One Ring\n1x Black Lotus\n2 Dragon Shield Matte Sleeves";

export default function DeckBuilderPage() {
  const { addToCart } = useShop();
  const [text, setText] = useState("");
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/deckbuilder", { text });
      setRows(data);
      setPick(Object.fromEntries(data.map((r, i) => [i, r.matches.find((m) => m.stock > 0)?.id || r.matches[0]?.id])));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const chosen = (rows || []).map((r, i) => ({ r, p: r.matches.find((m) => m.id === pick[i]) })).filter((x) => x.p && x.p.stock > 0);
  const total = chosen.reduce((s, { r, p }) => s + p.price * Math.min(r.quantity, p.stock), 0);
  const addAll = async () => { for (const { r, p } of chosen) await addToCart(p, Math.min(r.quantity, p.stock)); };

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-8 sm:px-6 sm:py-12">
      <p className="font-display text-sm uppercase tracking-[0.2em] text-cyan-600">Deck builder</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-5xl" data-testid="deck-title">Costruisci il tuo mazzo</h1>
      <p className="mt-3 text-stone-600">Incolla la tua lista (una carta per riga, es. “4 Ragavan”) e aggiungi tutto al carrello in un clic.</p>
      <div className="mt-8 grid gap-8 lg:grid-cols-[380px_1fr]">
        <div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={14} placeholder={EXAMPLE} data-testid="deck-input"
            className="w-full rounded-2xl border-2 border-black/10 bg-white p-4 font-mono text-sm outline-none focus:border-amber-500" />
          <div className="mt-3 flex gap-2">
            <button onClick={search} disabled={busy || text.trim().length < 2} data-testid="deck-search" className="sticker-btn flex-1 rounded-full bg-amber-500 py-3 font-display font-bold text-black disabled:opacity-50">{busy ? "Ricerca..." : "Cerca le carte"}</button>
            <button onClick={() => setText(EXAMPLE)} data-testid="deck-example" className="rounded-full border-2 border-black/10 px-4 text-sm font-semibold">Esempio</button>
          </div>
        </div>
        <div>
          {rows === null ? <p className="rounded-2xl border border-dashed border-black/15 p-10 text-center text-stone-500">I risultati appariranno qui.</p> : (
            <div className="space-y-3" data-testid="deck-results">
              {rows.map((r, i) => (
                <div key={i} className="rounded-xl border border-black/10 bg-white p-4" data-testid={`deck-row-${i}`}>
                  <p className="font-mono text-xs text-stone-500">{r.line}</p>
                  {r.matches.length === 0 ? <p className="mt-1 text-sm font-semibold text-rose-500">Non disponibile</p> : (
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <select value={pick[i]} onChange={(e) => setPick({ ...pick, [i]: e.target.value })} className="h-9 flex-1 rounded-lg border border-black/15 px-2 text-sm" data-testid={`deck-pick-${i}`}>
                        {r.matches.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.condition || m.category} — {eur(m.price)}{m.stock ? ` (${m.stock} disp.)` : m.in_other_carts ? " (nel carrello di un altro utente)" : " (esaurito)"}</option>)}
                      </select>
                      <span className="font-mono text-sm">× {r.quantity}</span>
                    </div>
                  )}
                </div>
              ))}
              <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-black bg-white p-5 shadow-[4px_4px_0_#000]">
                <p className="font-display text-lg font-bold">{chosen.length} carte disponibili · <span className="font-mono text-amber-600" data-testid="deck-total">{eur(total)}</span></p>
                <button onClick={addAll} disabled={!chosen.length} data-testid="deck-add-all" className="sticker-btn flex items-center gap-2 rounded-full bg-black px-6 py-3 font-bold text-white disabled:opacity-50"><ShoppingBag className="h-4 w-4" />Aggiungi tutto al carrello</button>
              </div>
              <p className="text-xs text-stone-500">Le quantità vengono limitate alla disponibilità. <Link to="/catalogo" className="underline">Sfoglia il catalogo</Link></p>
              </>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
